
import React, { useState } from "react";
import ReactPlayer from "react-player/lazy";

import {
  HiOutlineExclamationTriangle,
  HiOutlineVideoCamera,
  HiOutlineArrowUpRight,
} from "react-icons/hi2";

import { useTranslation } from "react-i18next";

/* ============================================
   VALIDATE YOUTUBE URL
============================================ */

function getSafeYouTubeUrl(value) {
  if (typeof value !== "string") return null;

  try {
    const url = new URL(value.trim());

    const allowedHosts = [
      "youtube.com",
      "www.youtube.com",
      "m.youtube.com",
      "youtu.be",
      "www.youtu.be",
      "www.youtube-nocookie.com",
    ];

    if (
      !["https:", "http:"].includes(url.protocol) ||
      !allowedHosts.includes(url.hostname)
    ) {
      return null;
    }

    return url.href;
  } catch {
    return null;
  }
}

/* ============================================
   RESPONSIVE YOUTUBE PLAYER
============================================ */

function ResponsiveVideoPlayer({
  link,
  title = "",
}) {
  const { i18n } = useTranslation();

  const [status, setStatus] = useState("loading");

  const isEnglish = (
    i18n.resolvedLanguage ||
    i18n.language ||
    "ar"
  )
    .toLowerCase()
    .startsWith("en");

  const videoUrl = getSafeYouTubeUrl(link);

  const content = {
    loading: isEnglish
      ? "Preparing your video..."
      : "جاري تجهيز الفيديو...",

    unavailable: isEnglish
      ? "Video Unavailable"
      : "تعذر تشغيل الفيديو",

    description: isEnglish
      ? "The video could not be loaded here. You can try watching it directly on YouTube."
      : "تعذر تحميل الفيديو داخل الموقع. يمكنك محاولة مشاهدته مباشرة عبر يوتيوب.",

    missing: isEnglish
      ? "No valid YouTube link is available for this video."
      : "لا يوجد رابط يوتيوب صالح لهذا الفيديو.",

    youtube: isEnglish
      ? "Open on YouTube"
      : "افتح الفيديو على يوتيوب",
  };

  /* ======================================
     EMPTY / INVALID URL
  ====================================== */

  if (!videoUrl) {
    return (
      <div className="relative flex aspect-video w-full flex-col items-center justify-center gap-4 bg-[#081827] px-7 text-center text-white">

        <HiOutlineVideoCamera
          size={46}
          className="text-[#D7B77F]"
        />

        <p className="font-kufi text-base font-semibold">
          {content.unavailable}
        </p>

        <p className="max-w-[400px] font-kufi text-sm leading-7 text-[#AFC3D1]">
          {content.missing}
        </p>

      </div>
    );
  }

  return (
    <div
      className="
        relative aspect-video
        w-full overflow-hidden
        bg-[#050D15]
      "
      aria-label={title}
    >
      {/* ======================================
          YOUTUBE PLAYER
      ====================================== */}

      <ReactPlayer
        url={videoUrl}
        controls={true}
        playing={false}
        width="100%"
        height="100%"
        className="!absolute !inset-0"
        onReady={() => setStatus("ready")}
        onError={() => setStatus("error")}
        config={{
          youtube: {
            playerVars: {
              rel: 0,
              modestbranding: 1,
              playsinline: 1,
            },
          },
        }}
      />

      {/* ======================================
          LOADING OVERLAY
      ====================================== */}

      {status === "loading" && (
        <div
          role="status"
          className="
            pointer-events-none
            absolute inset-0 z-10

            flex flex-col
            items-center justify-center
            gap-5

            bg-[#081827]
            text-white
          "
        >
          <div
            className="
              flex h-16 w-16
              items-center justify-center

              rounded-2xl
              border border-white/15
              bg-white/[0.06]

              text-[#D7B77F]
            "
          >
            <HiOutlineVideoCamera
              size={29}
              aria-hidden="true"
            />
          </div>

          <div
            aria-hidden="true"
            className="
              h-6 w-6 animate-spin
              rounded-full
              border-2 border-white/15
              border-t-[#D7B77F]
            "
          />

          <span className="font-kufi text-sm text-white/70">
            {content.loading}
          </span>
        </div>
      )}

      {/* ======================================
          ERROR STATE
      ====================================== */}

      {status === "error" && (
        <div
          className="
            absolute inset-0 z-20
            flex flex-col items-center
            justify-center gap-4

            bg-[#081827]
            px-6 py-8
            text-center text-white
          "
        >
          <HiOutlineExclamationTriangle
            size={43}
            className="text-[#D7B77F]"
          />

          <h3 className="font-kufi text-lg font-semibold">
            {content.unavailable}
          </h3>

          <p className="max-w-[460px] font-kufi text-sm leading-7 text-[#B5C8D6]">
            {content.description}
          </p>

          <a
            href={videoUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="
              mt-2 inline-flex min-h-[46px]
              items-center gap-3

              rounded-xl
              border border-white/20
              bg-white/10

              px-5 py-2.5

              font-kufi text-sm font-semibold

              transition-colors
              hover:bg-white/20

              focus-visible:outline-none
              focus-visible:ring-2
              focus-visible:ring-[#D7B77F]
            "
          >
            {content.youtube}

            <HiOutlineArrowUpRight
              size={18}
              className="rtl:-scale-x-100"
            />
          </a>
        </div>
      )}

    </div>
  );
}

export default ResponsiveVideoPlayer;
